/**
 * Cloud layers: presets, the resolved look of a layer, and the quality tiers.
 *
 * A layer stores only what the creator set (CloudLayerData, all look fields optional); every
 * field it leaves out comes from its preset, so a scene saved today keeps its look if a preset
 * default is tuned later only for fields the creator never touched. The generator and the
 * shader read the resolved CloudLook.
 */
import type { CloudLayerData, CloudPreset, CloudQuality, EntityAnimationConfig } from '../../types';

/** How a preset's clouds are built (cloudGenerator.ts). */
export type CloudShapeKind = 'cumulus' | 'stratus' | 'cirrus' | 'cumulonimbus' | 'fog';

export interface CloudShape {
    kind: CloudShapeKind;
    /** Cloud height / width. */
    aspect: number;
    /** Distance between cloud centres at full coverage, in cloud widths. */
    spacing: number;
    /** Spread of cloud sizes (log-normal sigma). */
    sizeVariance: number;
    /** Billow generations stacked on the base puffs (cumulus family). */
    billows: number;
    /** 0 = puffs spread sideways, 1 = they climb (towers). */
    tower: number;
    /** How strongly billowy noise erodes the edges (wispy, broken edges). */
    erosion: number;
    /** Vertical squash of each splat (1 = round, < 1 = flat). */
    squash: number;
    /** Elongation of each splat along the wind (cirrus streaks). */
    stretch: number;
    /** Per-splat opacity scale. */
    opacity: number;
    /** Splat size relative to the spacing between splats (overlap = softness). */
    overlap: number;
    /** Share of the budget a cloud of this kind gets per unit of volume (thin decks need fewer). */
    volumeWeight: number;
}

/** Everything the generator and the shader need, with every field filled. */
export interface CloudLook {
    preset: CloudPreset;
    coverage: number;
    density: number;
    cloudScale: number;
    windDirection: number;
    windSpeed: number;
    evolution: number;
    lifecycle: boolean;
    color: string;
    shadeColor: string;
    softness: number;
    seed: number;
    quality: CloudQuality;
    castShadows: boolean;
    shadowStrength: number;
    opacity: number;
    area: 'box' | 'dome';
    shape: CloudShape;
    /** Lighting: forward-scattering eccentricity (silver lining), back lobe, powder darkening, ambient. */
    phaseG: number;
    silver: number;
    powder: number;
    ambient: number;
    /** Toon-like banded light (the Stylised preset). */
    stylised: boolean;
}

type PresetFields = Omit<CloudLook, 'seed' | 'quality' | 'area' | 'opacity'>;

const CUMULUS_SHAPE: CloudShape = {
    kind: 'cumulus', aspect: 0.62, spacing: 1.35, sizeVariance: 0.32, billows: 2, tower: 0.35,
    erosion: 0.45, squash: 0.92, stretch: 1, opacity: 1, overlap: 0.95, volumeWeight: 1,
};

export const CLOUD_PRESETS: Record<CloudPreset, PresetFields> = {
    cumulus: {
        preset: 'cumulus', coverage: 0.55, density: 0.8, cloudScale: 40, windDirection: 20, windSpeed: 2,
        evolution: 0.35, lifecycle: false, color: '#ffffff', shadeColor: '#8d9bb3', softness: 0.45,
        castShadows: true, shadowStrength: 0.5, shape: CUMULUS_SHAPE,
        phaseG: 0.6, silver: 0.85, powder: 0.3, ambient: 1, stylised: false,
    },
    overcast: {
        preset: 'overcast', coverage: 0.92, density: 0.75, cloudScale: 90, windDirection: 20, windSpeed: 1.5,
        evolution: 0.2, lifecycle: false, color: '#e9ecf0', shadeColor: '#7d8592', softness: 0.75,
        castShadows: true, shadowStrength: 0.35,
        shape: { kind: 'stratus', aspect: 0.2, spacing: 0.7, sizeVariance: 0.2, billows: 0, tower: 0, erosion: 0.35, squash: 0.55, stretch: 1.2, opacity: 0.9, overlap: 1.05, volumeWeight: 0.55 },
        phaseG: 0.45, silver: 0.35, powder: 0.3, ambient: 1.15, stylised: false,
    },
    cirrus: {
        preset: 'cirrus', coverage: 0.6, density: 0.55, cloudScale: 80, windDirection: 35, windSpeed: 5,
        evolution: 0.25, lifecycle: false, color: '#ffffff', shadeColor: '#b4c2d8', softness: 0.7,
        castShadows: false, shadowStrength: 0.15,
        shape: { kind: 'cirrus', aspect: 0.08, spacing: 1.1, sizeVariance: 0.35, billows: 0, tower: 0, erosion: 0.6, squash: 0.3, stretch: 6, opacity: 0.6, overlap: 1.1, volumeWeight: 0.35 },
        phaseG: 0.7, silver: 1, powder: 0.1, ambient: 1.2, stylised: false,
    },
    storm: {
        preset: 'storm', coverage: 0.7, density: 0.95, cloudScale: 70, windDirection: 20, windSpeed: 3,
        evolution: 0.55, lifecycle: true, color: '#e8eaee', shadeColor: '#3f4658', softness: 0.45,
        castShadows: true, shadowStrength: 0.7,
        shape: { kind: 'cumulonimbus', aspect: 1.45, spacing: 1.7, sizeVariance: 0.3, billows: 3, tower: 0.85, erosion: 0.4, squash: 0.95, stretch: 1, opacity: 1.1, overlap: 0.95, volumeWeight: 1.2 },
        phaseG: 0.55, silver: 0.7, powder: 0.45, ambient: 0.8, stylised: false,
    },
    fog: {
        preset: 'fog', coverage: 0.85, density: 0.55, cloudScale: 30, windDirection: 20, windSpeed: 0.6,
        evolution: 0.3, lifecycle: false, color: '#e6eaee', shadeColor: '#c3cad3', softness: 1,
        castShadows: false, shadowStrength: 0.2,
        shape: { kind: 'fog', aspect: 0.16, spacing: 0.6, sizeVariance: 0.25, billows: 0, tower: 0, erosion: 0.4, squash: 0.32, stretch: 1.6, opacity: 0.4, overlap: 1.6, volumeWeight: 0.6 },
        phaseG: 0.35, silver: 0.2, powder: 0.05, ambient: 1.15, stylised: false,
    },
    stylised: {
        preset: 'stylised', coverage: 0.5, density: 1, cloudScale: 45, windDirection: 20, windSpeed: 2.5,
        evolution: 0.4, lifecycle: false, color: '#fff8f0', shadeColor: '#b4b9e6', softness: 0.3,
        castShadows: true, shadowStrength: 0.45,
        shape: { ...CUMULUS_SHAPE, aspect: 0.7, billows: 1, erosion: 0.15, opacity: 1.5, overlap: 0.85, sizeVariance: 0.25 },
        phaseG: 0.5, silver: 1.2, powder: 0.2, ambient: 1, stylised: true,
    },
};

export const CLOUD_PRESET_LABELS: Record<CloudPreset, string> = {
    cumulus: 'Fair-weather cumulus',
    overcast: 'Overcast',
    cirrus: 'Wispy cirrus',
    storm: 'Storm build-up',
    fog: 'Low fog bank',
    stylised: 'Stylised',
};

export const CLOUD_PRESET_ORDER: CloudPreset[] = ['cumulus', 'overcast', 'cirrus', 'storm', 'fog', 'stylised'];

export function isCloudPreset(value: unknown): value is CloudPreset {
    return typeof value === 'string' && value in CLOUD_PRESETS;
}

const num = (value: unknown, fallback: number, lo = -Infinity, hi = Infinity): number => {
    const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    return Math.min(hi, Math.max(lo, n));
};
const hex = (value: unknown, fallback: string): string =>
    typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : fallback;

/** The layer's full look: preset defaults, overridden by what the layer sets. */
export function resolveCloudLook(data: Partial<CloudLayerData> | null | undefined): CloudLook {
    const d = data ?? {};
    const presetKey: CloudPreset = isCloudPreset(d.preset) ? d.preset : 'cumulus';
    const p = CLOUD_PRESETS[presetKey];
    const quality: CloudQuality = d.quality === 'low' || d.quality === 'medium' || d.quality === 'high' ? d.quality : 'auto';
    return {
        ...p,
        preset: presetKey,
        coverage: num(d.coverage, p.coverage, 0, 1),
        density: num(d.density, p.density, 0, 1),
        cloudScale: num(d.cloudScale, p.cloudScale, 0.05, 100000),
        windDirection: num(d.windDirection, p.windDirection),
        windSpeed: num(d.windSpeed, p.windSpeed, 0, 10000),
        evolution: num(d.evolution, p.evolution, 0, 1),
        lifecycle: typeof d.lifecycle === 'boolean' ? d.lifecycle : p.lifecycle,
        color: hex(d.color, p.color),
        shadeColor: hex(d.shadeColor, p.shadeColor),
        softness: num(d.softness, p.softness, 0, 1),
        seed: Math.trunc(num(d.seed, 1, -2147483648, 2147483647)),
        quality,
        castShadows: typeof d.castShadows === 'boolean' ? d.castShadows : p.castShadows,
        shadowStrength: num(d.shadowStrength, p.shadowStrength, 0, 1),
        opacity: num(d.opacity, 1, 0, 1),
        area: d.area === 'dome' ? 'dome' : 'box',
    };
}

/** Fields a preset writes into a layer when the creator picks it (the editor's preset buttons). */
export function cloudPresetPatch(preset: CloudPreset): Partial<CloudLayerData> {
    const p = CLOUD_PRESETS[preset];
    return {
        preset,
        coverage: p.coverage,
        density: p.density,
        windSpeed: p.windSpeed,
        evolution: p.evolution,
        lifecycle: p.lifecycle,
        color: p.color,
        shadeColor: p.shadeColor,
        softness: p.softness,
        castShadows: p.castShadows,
        shadowStrength: p.shadowStrength,
    };
}

// ── quality tiers ───────────────────────────────────────────────────────────────────────

export type CloudTier = 'high' | 'medium' | 'low';

export interface CloudQualityInputs {
    /** Phone / tablet / headset GPU class (or the editor's mobile preview). */
    mobile: boolean;
    /** The viewer's performance mode. */
    performanceMode: boolean;
    /** An immersive XR session is running. */
    xr: boolean;
    /** Diagnostics override (`?cloudQuality=high|medium|low`). */
    forced?: CloudTier | null;
}

/** Splats per layer for each tier. Fewer splats are drawn larger, so a layer keeps its shape. */
export const CLOUD_TIER_BUDGETS: Record<CloudTier, number> = {
    high: 160_000,
    medium: 70_000,
    low: 24_000,
};

/** All layers together never exceed this many splats on a tier (each layer is scaled down). */
export const CLOUD_TIER_TOTALS: Record<CloudTier, number> = {
    high: 320_000,
    medium: 140_000,
    low: 48_000,
};

/** The tier this device renders clouds at. */
export function selectCloudTier(inputs: CloudQualityInputs): CloudTier {
    if (inputs.forced === 'high' || inputs.forced === 'medium' || inputs.forced === 'low') return inputs.forced;
    if (inputs.xr || inputs.mobile) return 'low';
    if (inputs.performanceMode) return 'medium';
    return 'high';
}

/**
 * A layer's splat budget: its own quality setting caps the device tier (a layer set to Low is
 * low everywhere; Auto follows the device), then the layers share the tier's total.
 */
export function cloudLayerBudget(quality: CloudQuality, tier: CloudTier, layerCount = 1): number {
    const order: CloudTier[] = ['low', 'medium', 'high'];
    const effective = quality === 'auto' ? tier : order[Math.min(order.indexOf(quality), order.indexOf(tier))];
    const own = CLOUD_TIER_BUDGETS[effective];
    const share = Math.floor(CLOUD_TIER_TOTALS[tier] / Math.max(1, layerCount));
    return Math.max(2000, Math.min(own, share));
}

/** A small, stable seed for new layers and "re-roll". */
export function randomCloudSeed(random: () => number = Math.random): number {
    return 1 + Math.floor(random() * 999_999);
}

/** Wind direction (degrees, scene XZ: 0 = +X, 90 = +Z) as a unit vector in scene coordinates. */
export function windVector(directionDeg: number): { x: number; z: number } {
    const a = (directionDeg * Math.PI) / 180;
    return { x: Math.cos(a), z: Math.sin(a) };
}

/**
 * A default layer for a scene whose content spans `bounds` (scene units): a box over the scene,
 * a bit wider than it, floating above its top, with clouds sized to the scene.
 */
/** Per axis, the largest |scale| a layer's keyframes reach; null where that axis isn't keyframed. */
export interface CloudScaleExtent {
    x: number | null;
    y: number | null;
    z: number | null;
}

/**
 * The largest size the timeline gives a cloud layer's box, from its enabled clips' scale
 * keyframes, or null when its size isn't keyframed. A layer with a keyframed size generates once
 * for that size; the live box is then a window into it (growing reveals clouds, shrinking hides
 * them), so the editor timeline and the published viewer show the same thing with no stretching
 * and no regeneration mid-animation.
 */
export function cloudScaleKeyframeExtent(animations: ReadonlyArray<EntityAnimationConfig> | null | undefined, layerId: string): CloudScaleExtent | null {
    const out: CloudScaleExtent = { x: null, y: null, z: null };
    let any = false;
    for (const anim of animations ?? []) {
        if (!anim || anim.enabled === false || anim.entityType !== 'cloudLayer' || anim.entityId !== layerId) continue;
        for (const track of anim.tracks ?? []) {
            const axis = track?.property === 'scale.x' ? 'x' : track?.property === 'scale.y' ? 'y' : track?.property === 'scale.z' ? 'z' : null;
            if (!axis) continue;
            for (const key of track.keyframes ?? []) {
                const v = Math.abs(Number(key?.value));
                if (!Number.isFinite(v)) continue;
                out[axis] = Math.max(out[axis] ?? 0, v);
                any = true;
            }
        }
    }
    return any ? out : null;
}

/**
 * The box a layer generates for: its own size, grown per axis to the largest size its keyframes
 * reach. A dome uses its x size (the diameter) for both x and z.
 */
export function cloudGenerationBox(
    scale: { x: number; y: number; z: number },
    extent: CloudScaleExtent | null,
    dome: boolean,
): { x: number; y: number; z: number } {
    const at = (v: number, k: number | null) => Math.max(1e-4, Math.abs(Number.isFinite(v) ? v : 0), k ?? 0);
    const x = at(scale.x, extent?.x ?? null);
    const y = at(scale.y, extent?.y ?? null);
    const z = dome ? x : at(scale.z, extent?.z ?? null);
    return { x, y, z };
}

/**
 * The thickness a layer needs for a preset to show its shape, or null when any thickness works.
 * Storm clouds tower (taller than wide): squeezed into a thin layer they come out as small tufts.
 */
export function cloudPresetMinThickness(preset: CloudPreset, cloudScale: number | undefined): number | null {
    if (preset !== 'storm') return null;
    const scale = Number.isFinite(cloudScale) && (cloudScale as number) > 0 ? (cloudScale as number) : CLOUD_PRESETS.storm.cloudScale;
    return scale * CLOUD_PRESETS.storm.shape.aspect;
}

/**
 * The patch that switches a layer to a preset: its look, plus (for a storm in a thin layer) the
 * thickness its towers need, grown upwards so the cloud base stays where it was.
 */
export function cloudPresetPatchFor(layer: Pick<CloudLayerData, 'position' | 'scale' | 'cloudScale'>, preset: CloudPreset): Partial<CloudLayerData> {
    const patch: Partial<CloudLayerData> = cloudPresetPatch(preset);
    const need = cloudPresetMinThickness(preset, layer.cloudScale);
    if (need !== null && layer.scale.y < need * 0.9) {
        const grow = need - layer.scale.y;
        patch.scale = { ...layer.scale, y: round2(need) };
        patch.position = { ...layer.position, y: round2(layer.position.y + grow / 2) };
    }
    return patch;
}

export function defaultCloudLayerFor(
    bounds: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } | null,
    preset: CloudPreset = 'cumulus',
): Pick<CloudLayerData, 'position' | 'rotation' | 'scale' | 'cloudScale'> {
    const min = bounds?.min ?? { x: -20, y: 0, z: -20 };
    const max = bounds?.max ?? { x: 20, y: 10, z: 20 };
    const sx = Math.max(1, max.x - min.x);
    const sy = Math.max(0.5, max.y - min.y);
    const sz = Math.max(1, max.z - min.z);
    const span = Math.max(sx, sz);
    const cx = (min.x + max.x) / 2;
    const cz = (min.z + max.z) / 2;
    const width = span * 1.8;
    if (preset === 'fog') {
        const thickness = Math.max(sy * 0.25, span * 0.04);
        return {
            position: { x: cx, y: min.y + thickness * 0.5, z: cz },
            rotation: { x: 0, y: 0, z: 0 },
            scale: { x: width, y: thickness, z: width },
            cloudScale: round2(span * 0.18),
        };
    }
    const thickness = preset === 'storm' ? span * 0.5 : preset === 'cirrus' ? span * 0.08 : span * 0.22;
    const altitude = max.y + (preset === 'cirrus' ? span * 0.6 : span * 0.25) + thickness * 0.5;
    return {
        position: { x: cx, y: altitude, z: cz },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: width, y: thickness, z: width },
        cloudScale: round2(span * (preset === 'overcast' ? 0.45 : preset === 'cirrus' ? 0.4 : preset === 'storm' ? 0.35 : 0.22)),
    };
}

function round2(v: number): number {
    return Math.round(v * 100) / 100;
}

/** Inputs that force a fresh generation when they change (everything else is a uniform). */
export const CLOUD_STRUCTURAL_KEYS = ['preset', 'cloudScale', 'seed', 'quality', 'area'] as const;

/** True when two looks need different geometry (everything else is a uniform: no regeneration). */
export function cloudGeometryChanged(a: CloudLook, b: CloudLook): boolean {
    return CLOUD_STRUCTURAL_KEYS.some((k) => a[k] !== b[k]);
}

/** sRGB hex → linear RGB. */
export function cloudHexToLinear(value: string, fallback: [number, number, number] = [1, 1, 1]): [number, number, number] {
    if (!/^#[0-9a-f]{6}$/i.test(value)) return fallback;
    const c = [1, 3, 5].map((i) => parseInt(value.slice(i, i + 2), 16) / 255);
    return c.map((v) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4))) as [number, number, number];
}

/** sRGB hex → sRGB 0..1 triple (animation keys use sRGB channels). */
export function cloudHexToSrgb(value: string): [number, number, number] {
    if (!/^#[0-9a-f]{6}$/i.test(value)) return [1, 1, 1];
    return [1, 3, 5].map((i) => parseInt(value.slice(i, i + 2), 16) / 255) as [number, number, number];
}

export function cloudSrgbToHex(rgb: [number, number, number]): string {
    return `#${rgb.map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('')}`;
}
